// toast-helper.ts

interface ToastOptions {
  message: string;
  duration?: number;
  type?: 'success' | 'error' | 'warning' | 'info';
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

class ToastHelper {
  private static instance: ToastHelper;
  private container: HTMLDivElement;

  private constructor() {
    // Create container for toasts
    this.container = document.createElement('div');
    this.container.style.position = 'fixed';
    this.container.style.zIndex = '9999';
    document.body.appendChild(this.container);
  }

  public static getInstance(): ToastHelper {
    if (!ToastHelper.instance) {
      ToastHelper.instance = new ToastHelper();
    }
    return ToastHelper.instance;
  }

  private getTypeStyles(type: string = 'info'): { bgColor: string; progressBarColor: string } {
    const styles = {
      success: {
        bgColor: 'var(--text-success-color)',
        progressBarColor: 'var(--bg-primary-color)'
      },
      error: {
        bgColor: 'var(--text-danger-color)',
        progressBarColor: '#da190b'
      },
      warning: {
        bgColor: 'var(--text-warning-color)',
        progressBarColor: '#e68900'
      },
      info: {
        bgColor: 'var(--text-info-color)',
        progressBarColor: '#0b7dda'
      }
    };
    return styles[type] || styles.info;
  }

  private setPosition(position: string = 'top-right'): void {
    const positions = {
      'top-right': { top: '20px', right: '20px', bottom: 'auto', left: 'auto' },
      'top-left': { top: '20px', right: 'auto', bottom: 'auto', left: '20px' },
      'bottom-right': { top: 'auto', right: '20px', bottom: '20px', left: 'auto' },
      'bottom-left': { top: 'auto', right: 'auto', bottom: '20px', left: '20px' },
      'top-center': { top: '20px', right: 'auto', bottom: 'auto', left: '50%', transform: 'translateX(-50%)' },
      'bottom-center': { top: 'auto', right: 'auto', bottom: '20px', left: '50%', transform: 'translateX(-50%)' }
    };

    Object.assign(this.container.style, positions[position]);
  }

  public show(options: ToastOptions): void {
    const {
      message,
      duration = 3000,
      type = 'info',
      position = 'top-right'
    } = options;

    // Set container position
    this.setPosition(position);

    // Create toast element
    const toast = document.createElement('toast-notification');
    const styles = this.getTypeStyles(type);

    // Set toast properties
    toast.setAttribute('bgColor', styles.bgColor);
    toast.setAttribute('color', 'var(--bg-primary-color)');
    toast.setAttribute('progressBarColor', 'var(--bg-primary-color)');
    toast.setAttribute('toastMessage', message);
    toast.setAttribute('padding', '12px');
    toast.setAttribute('borderRadius', '5px');
    toast.setAttribute('width', '300px');
    toast.style.marginBottom = '10px';
    toast.style.display = 'block';

    // Add toast to container
    this.container.appendChild(toast);

    // Remove toast after duration
    setTimeout(() => {
      if (toast && toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, duration);
  }
}

// Export the helper function
export const showToast = (options: ToastOptions): void => {
  ToastHelper.getInstance().show(options);
};